import type { EntityPermission } from '@sportninja/common/services/event/getEvent'

interface UseEventPermissionArgs {
  /** `meta.permissions[eventId]` from the event response. */
  eventPermission?: EntityPermission | null
}

interface UseEventPermissionReturn {
  /**
   * May open the event at all.
   *
   * Unlike edit/delete this DEFAULTS TO TRUE when no permission object is
   * present. Denying by default is right for an action — briefly hiding a
   * button beats showing one that 403s — but wrong for access: a response that
   * simply omits `meta.permissions` would lock every viewer out of an event
   * they can legitimately read. The API is the real gate and answers 403 when
   * it means it; this flag only lets the page say so without a failed request.
   */
  canView: boolean
  /** May edit the event. */
  canEdit: boolean
  /** May delete the event. */
  canDelete: boolean
  /**
   * Kept for callers that render nothing until the answer is known. Always
   * false now that the answer arrives with the event itself — the event
   * response IS the source, so there is no separate request to wait on.
   */
  isLoading: boolean
}

const NONE: UseEventPermissionReturn = {
  // Permissive on view, denying on the actions — see `canView` above.
  canView: true,
  canEdit: false,
  canDelete: false,
  isLoading: false,
}

/**
 * Whether the current user may edit or delete an event.
 *
 * Events have no permissions of their own — the API checks against the parent
 * team, and "anyone who can manage the parent" qualifies. That answer is
 * returned with the event itself, as `meta.permissions[eventId]`, which
 * `getEvent` surfaces rather than discarding.
 *
 * Defaults to DENY: whenever no flags resolve, both capabilities are false.
 * Showing a control that 403s is worse than briefly hiding one the user has.
 */
export const useEventPermission = ({
  eventPermission,
}: UseEventPermissionArgs): UseEventPermissionReturn => {
  'use memo'

  if (!eventPermission) {
    return NONE
  }

  return {
    // `view` OR `viewAny` OR `admin`: the API sends all three, and any one of
    // them is a grant. An explicit permission object that grants none of them
    // is a real denial — that is the case this gate exists for.
    canView: Boolean(
      eventPermission.view || eventPermission.viewAny || eventPermission.admin
    ),
    canEdit: Boolean(eventPermission.update || eventPermission.admin),
    canDelete: Boolean(eventPermission.delete || eventPermission.admin),
    isLoading: false,
  }
}
